import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Api from "../../../services/api";
import { Card, DatePicker, Table, Row, Col, Button, Space, Tag, Typography, Input, Select, Drawer } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

const { RangePicker } = DatePicker;
const { Text } = Typography;

function RevenueIAPMonetization(){
  const { code, os } = useParams();

  const [dateRange, setDateRange] = useState(() => [
    dayjs(moment().subtract(7, 'days').format('YYYY-MM-DD')),
    dayjs(moment().format('YYYY-MM-DD'))
  ]);
  const [skuId, setSkuId] = useState('');
  const [skuTitle, setSkuTitle] = useState('');
  const [status, setStatus] = useState();
  const [country, setCountry] = useState();
  const [countryOptions, setCountryOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [sorter, setSorter] = useState({ columnKey: 'begin_date', order: 'descend' });
  const [appliedFilters, setAppliedFilters] = useState(() => ({
    from: dayjs(moment().subtract(7, 'days').format('YYYY-MM-DD')),
    to: dayjs(moment().format('YYYY-MM-DD')),
    sku_id: '',
    sku_title: '',
    financial_status: '',
    country: '',
  }));

  const params = useMemo(() => {
    const from = appliedFilters?.from?.format('YYYY-MM-DD');
    const to = appliedFilters?.to?.format('YYYY-MM-DD');
    const sort_by_column = sorter?.columnKey || 'begin_date';
    const sort_by_column_value = sorter?.order === 'ascend' ? 'asc' : 'desc';
    // console.log('[Revenue IAP] params useMemo - sorter:', sorter, 'sort_by_column:', sort_by_column, 'sort_by_column_value:', sort_by_column_value);
    return {
      from,
      to,
      code,
      os,
      sku_id: appliedFilters?.sku_id || '',
      sku_title: appliedFilters?.sku_title || '',
      financial_status: appliedFilters?.financial_status || '',
      country: appliedFilters?.country || '',
      page: pagination.current,
      sort_by_column,
      sort_by_column_value,
    };
  }, [appliedFilters, code, os, pagination.current, sorter]);

  const abortRef = useRef(null);
  const requestIdRef = useRef(0);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const search = new URLSearchParams(params).toString();
      try { 
        // console.log('[Revenue IAP] query params:', params, 'query:', search); 
      } catch(e) {}
      if (abortRef.current) {
        try { abortRef.current.abort(); } catch(e) {}
      }
      const controller = new AbortController();
      abortRef.current = controller;
      const currentId = ++requestIdRef.current;
      const res = await Api.get(`/api/monetization/revenue_iap?${search}`, { signal: controller.signal });
      if (currentId !== requestIdRef.current) {
        return; // response cũ, bỏ qua
      }
      if (res?.data?.code === 0) {
        const items = Array.isArray(res.data.data) ? res.data.data : [];
        const withKey = items.map((i) => ({ ...i, key: i.id }));
        setData(withKey);
        setPagination((prev) => ({
          ...prev,
          total: Number(res.data.total || 0),
          pageSize: Number(res.data.limit || prev.pageSize || 20),
          current: Number(res.data.page || prev.current || 1)
        }));
      } else {
        toast.error('Không lấy được dữ liệu');
        setData([]);
      }
    } catch (e) {
      console.error(e);
      toast.error('Lỗi tải dữ liệu');
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    fetchData();
  }, [params]);

  // useEffect(() => {
  //   console.log('[Revenue IAP] sorter state changed:', sorter);
  // }, [sorter]);

  const onTableChange = useCallback((pag, _filters, sorterArg) => {
    setPagination((prev) => ({ ...prev, current: pag?.current || 1, pageSize: pag?.pageSize || prev.pageSize }));
  }, []);

  const handleRun = () => {
    setAppliedFilters({
      from: dateRange?.[0],
      to: dateRange?.[1],
      sku_id: skuId?.trim() || '',
      sku_title: skuTitle?.trim() || '',
      financial_status: status || '',
      country: country || '',
    });
    setPagination((p) => ({ ...p, current: 1 }));
  };

  const nowrapCell = useMemo(() => ({ style: { whiteSpace: 'nowrap' } }), []);
  const centerHeader = useMemo(() => ({ style: { textAlign: 'center', whiteSpace: 'nowrap' } }), []);
  const columns = useMemo(() => [
    { title: 'Date', dataIndex: 'begin_date', key: 'begin_date', columnKey: 'begin_date', fixed: 'left', width: 160, align: 'center', onHeaderCell: () => centerHeader, onCell: () => nowrapCell, render: (v) => v ? dayjs(v).format('YYYY-MM-DD') : '' },
    { title: 'Store', dataIndex: 'store_name', key: 'store_name', columnKey: 'store_name', width: 140, align: 'center', onHeaderCell: () => centerHeader, onCell: () => nowrapCell, render: (v) => v ? <Tag color="blue">{v}</Tag> : '' },
    { title: 'SKU ID', dataIndex: 'sku_id', key: 'sku_id', columnKey: 'sku_id', width: 360, align: 'center', onHeaderCell: () => centerHeader, onCell: () => nowrapCell },
    { title: 'SKU Title', dataIndex: 'sku_title', key: 'sku_title', columnKey: 'sku_title', width: 420, align: 'center', onHeaderCell: () => centerHeader, onCell: () => nowrapCell },
    { title: 'Price', dataIndex: 'item_price', key: 'item_price', columnKey: 'item_price', align: 'center', width: 140, onHeaderCell: () => centerHeader, onCell: () => nowrapCell, render: (v, r) => `$${Number(v || 0).toFixed(2)} ${r.currency || ''}` },
    { title: 'Taxes', dataIndex: 'taxes', key: 'taxes', columnKey: 'taxes', align: 'center', width: 120, onHeaderCell: () => centerHeader, onCell: () => nowrapCell, render: (v) => Number(v || 0).toFixed(2) },
    { title: 'Revenue USD', dataIndex: 'revenue_usd', key: 'revenue_usd', columnKey: 'revenue_usd', align: 'center', width: 170, onHeaderCell: () => centerHeader, onCell: () => nowrapCell, render: (v) => `$${Number(v || 0).toFixed(2)}` },
    { title: 'Revenue', dataIndex: 'revenue', key: 'revenue', columnKey: 'revenue', align: 'center', width: 160, onHeaderCell: () => centerHeader, onCell: () => nowrapCell, render: (v) => `$${Number(v || 0).toFixed(2)}` },
    { title: 'Currency', dataIndex: 'currency', key: 'currency', width: 120, align: 'center', onHeaderCell: () => centerHeader, onCell: () => nowrapCell },
    { title: 'Country', dataIndex: 'country_code', key: 'country_code', width: 120, align: 'center', onHeaderCell: () => centerHeader, onCell: () => nowrapCell },
    { title: 'City', dataIndex: 'city_of_buyer', key: 'city_of_buyer', width: 200, align: 'center', onHeaderCell: () => centerHeader, onCell: () => nowrapCell },
    { title: 'Order No.', dataIndex: 'order_number', key: 'order_number', width: 300, align: 'center', onHeaderCell: () => centerHeader, onCell: () => nowrapCell },
    { title: 'Product Type', dataIndex: 'product_type_identifier', key: 'product_type_identifier', width: 160, align: 'center', onHeaderCell: () => centerHeader, onCell: () => nowrapCell },
    { title: 'Device', dataIndex: 'device_model', key: 'device_model', width: 140, align: 'center', onHeaderCell: () => centerHeader, onCell: () => nowrapCell },
    { title: 'Status', dataIndex: 'financial_status', key: 'financial_status', width: 140, align: 'center', onHeaderCell: () => centerHeader, onCell: () => nowrapCell, render: (v) => {
      if (!v) return '';
      const color = v === 'Charged' ? 'green' : v === 'Refund' ? 'volcano' : 'default';
      return <Tag color={color}>{v}</Tag>;
    } },
  ], [nowrapCell, centerHeader]);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res = await Api.get(`/api/app/dimension_filter?dimension=country`);
        const arr = Array.isArray(res?.data?.data) ? res.data.data : [];
        const opts = arr.map((c) => ({ value: c.value ?? c, label: c.label ?? c }));
        setCountryOptions(opts);
      } catch (e) {
        setCountryOptions([]);
      }
    };
    fetchCountries();
  }, []);

  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <>
    <div className='background-second-dashboard'>
      <section className="main__content-dashbard">
        <div className="container-fluid">
          <Card bodyStyle={{ padding: 20 }}>
            <div className="overview_content pd-20">
              {/* Mobile: floating search icon */}
              <div className="mobile-search-toggle" style={{ display: 'none', position: 'fixed', bottom: '20px', right: '20px', zIndex: 1000 }}>
                <Button type="primary" shape="circle" icon={<SearchOutlined />} onClick={() => setDrawerOpen(true)} style={{ borderRadius: '50%', width: 48, height: 48 }} />
              </div>
              <div className="mb-10 hd-filter desktop-filter">
                <Row gutter={[12, 12]}>
                  <Col xs={24} sm={12} md={8} lg={6} xl={5} xxl={5}>
                    <div style={{ marginBottom: 4 }}>SKU ID:</div>
                    <Input placeholder="Search by SKU ID" value={skuId} onChange={(e) => setSkuId(e.target.value)} />
                  </Col>
                  <Col xs={24} sm={12} md={8} lg={6} xl={5} xxl={5}>
                    <div style={{ marginBottom: 4 }}>SKU Title:</div>
                    <Input placeholder="Search by SKU Title" value={skuTitle} onChange={(e) => setSkuTitle(e.target.value)} />
                  </Col>
                  <Col xs={24} sm={12} md={8} lg={4} xl={3} xxl={3}>
                    <div style={{ marginBottom: 4 }}>Status:</div>
                    <Select
                      placeholder="Select status"
                      allowClear
                      showSearch
                      value={status}
                      onChange={setStatus}
                      optionFilterProp="label"
                      options={[
                        { value: 'Charged', label: 'Charged' },
                        { value: 'Refund', label: 'Refund' },
                      ]}
                      style={{ width: '100%' }}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={8} lg={4} xl={4} xxl={4}>
                    <div style={{ marginBottom: 4 }}>Country:</div>
                    <Select
                      placeholder="All Countries"
                      allowClear
                      showSearch
                      options={countryOptions}
                      value={country}
                      onChange={setCountry}
                      optionFilterProp="label"
                      filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes((input ?? '').toLowerCase())}
                      filterSort={(a, b) => String(a.label ?? '').localeCompare(String(b.label ?? ''))}
                      style={{ width: '100%' }}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={8} lg={6} xl={5} xxl={5}>
                    <div style={{ marginBottom: 4 }}>Date range:</div>
                    <RangePicker value={dateRange} onChange={(vals) => setDateRange(vals)} allowClear={false} style={{ width: '100%' }} />
                  </Col>
                  <Col xs={24} sm={12} md={4} lg={4} xl={2} xxl={2}>
                    <div style={{ marginBottom: 4 }}>&nbsp;</div>
                    <Button type="primary" block onClick={handleRun}>Run Report</Button>
                  </Col>
                </Row>
              </div>
              <Table
                rowKey="id"
                loading={loading}
                columns={columns}
                dataSource={data}
                pagination={{
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  total: pagination.total,
                  showSizeChanger: false,
                  // showTotal: (t) => `Total: ${t}`,
                }}
                onChange={onTableChange}
                scroll={{ x: 'max-content' }}
                bordered
              />
            </div>
          </Card>
        </div>
      </section>
    </div>
    {/* Mobile Drawer */}
    <Drawer
      title="Search Filters"
      placement="right"
      onClose={() => setDrawerOpen(false)}
      open={drawerOpen}
      width={320}
      className="mobile-filter-drawer"
      zIndex={2500}
    >
      <Row gutter={[12, 12]}>
        <Col xs={24}>
          <div style={{ marginBottom: 4 }}>SKU ID:</div>
          <Input placeholder="Search by SKU ID" value={skuId} onChange={(e) => setSkuId(e.target.value)} />
        </Col>
        <Col xs={24}>
          <div style={{ marginBottom: 4 }}>SKU Title:</div>
          <Input placeholder="Search by SKU Title" value={skuTitle} onChange={(e) => setSkuTitle(e.target.value)} />
        </Col>
        <Col xs={24}>
          <div style={{ marginBottom: 4 }}>Status:</div>
          <Select
            placeholder="Select status"
            allowClear
            showSearch
            value={status}
            onChange={setStatus}
            optionFilterProp="label"
            options={[{ value: 'Charged', label: 'Charged' },{ value: 'Refund', label: 'Refund' }]}
            style={{ width: '100%' }}
          />
        </Col>
        <Col xs={24}>
          <div style={{ marginBottom: 4 }}>Country:</div>
          <Select
            placeholder="All Countries"
            allowClear
            showSearch
            options={countryOptions}
            value={country}
            onChange={setCountry}
            optionFilterProp="label"
            filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes((input ?? '').toLowerCase())}
            filterSort={(a, b) => String(a.label ?? '').localeCompare(String(b.label ?? ''))}
            style={{ width: '100%' }}
          />
        </Col>
        <Col xs={24}>
          <div style={{ marginBottom: 4 }}>Date range:</div>
          <RangePicker value={dateRange} onChange={(vals) => setDateRange(vals)} allowClear={false} style={{ width: '100%' }} getPopupContainer={(t)=>t.parentNode} dropdownClassName="drawer-date-dropdown" />
        </Col>
        <Col xs={24}>
          <Button type="primary" block onClick={() => { handleRun(); setDrawerOpen(false); }}>Apply</Button>
        </Col>
      </Row>
    </Drawer>
    <style>{`
      @media (max-width: 768px) {
        .desktop-filter { display: none !important; }
        .mobile-search-toggle { display: block !important; }
        .mobile-filter-drawer .ant-drawer-body { padding: 10px !important; }
        .mobile-filter-drawer .drawer-date-dropdown { max-width: 100% !important; }
        .mobile-filter-drawer .drawer-date-dropdown .ant-picker-panels { flex-direction: column !important; }
      }
      @media (min-width: 769px) {
        .mobile-search-toggle { display: none !important; }
        .desktop-filter { display: block !important; }
      }
    `}</style>
    </>
  );
}
export default RevenueIAPMonetization;